import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { DSSetup, Service } from 'webapp-types';
import { BlockRenderStep } from 'webapp/enums/enums';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';

import type BlockRenderer from 'webapp/models-custom/block-renderer';
import type BlockState from 'webapp/models-custom/block-state';

interface Args {
	blockState: BlockState;
	blockRenderer: BlockRenderer;
	isUnselectable: boolean;
}

export default class BlockContentEmptyComponent extends Component<Args> {
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('navigation') declare navigationService: Service.Navigation;

	get block() {
		return this.args.blockState.block;
	}

	get blockSetup() {
		return this.block.blockSetup;
	}

	get visualization() {
		return this.blockSetup.visualization;
	}

	get isCustomMetricPreview() {
		return this.block.isFromCustomQuery || this.block.isFromDataCalculation;
	}

	get emptyText() {
		if (this.isCustomMetricPreview && !this.block.returnedData) {
			return 'Run a preview to see data';
		} else {
			return 'No data to display';
		}
	}

	get emptyImageUrl() {
		if (this.navigationService.isLoadedFromSnapshooter) {
			return '';
		} else {
			return this.mmBlocksService.getVisualizationTypeIconPath(
				this.visualization.visualizationType,
				'empty',
			);
		}
	}

	get isNoMetricSelected() {
		const firstDataSelect = this.blockSetup.firstShownDataSelect;
		return (
			!this.args.isUnselectable &&
			!this.isCustomMetricPreview &&
			firstDataSelect &&
			!firstDataSelect?.metricSetupKey
		);
	}

	@action
	openMetricSelect() {
		void this.mmMetricsService.openMetricSelectPopup({
			onChange: (metricData: DSSetup.MetricData) => {
				const firstDataSelect = this.blockSetup.firstShownDataSelect;
				if (!firstDataSelect) {
					return;
				}
				this.blockSetup.changeMetric(firstDataSelect, metricData);
			},
		});
	}

	@action
	setupView(node: HTMLElement) {
		// bind content renderer handlers
		const blockRenderer = this.args.blockRenderer;
		blockRenderer.registerStepHandler(BlockRenderStep.CONTENT, this, () => {
			this.updateEmptyImageSize(node);
			return Promise.resolve();
		});

		this.updateEmptyImageSize(node);

		// prepare functions for teardown, since they dont have access to this.args
		this.teardownFns.push(() => {
			blockRenderer.unregisterStepHandler(BlockRenderStep.CONTENT, this);
		});
	}

	private updateEmptyImageSize(node: HTMLElement) {
		const availableHeight = this.args.blockRenderer.getHeightForNumbersAndContent();
		const image = node.querySelector<HTMLElement>('.bc__empty-image');
		if (image) {
			const prop =
				this.visualization.isBarChart || this.visualization.isLineChart
					? 'height' // using whole height, stretching the svg (explicitly marked with preserveAspectRatio="none")
					: 'maxHeight';
			image.style[prop] = `${Math.floor(availableHeight)}px`;
		}
	}

	private teardownFns: (() => void)[] = [];

	@action
	teardownView(): void {
		this.teardownFns.forEach((fn) => fn());
	}
	<template>
		<div
			class="bc__empty-container"
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
		>
			{{#if this.emptyImageUrl}}
				<img class="bc__empty-image" src={{this.emptyImageUrl}} alt />
			{{/if}}
			<div class="bc__empty-inner">
				{{#if this.isNoMetricSelected}}
					<a class="button" {{on "click" this.openMetricSelect}}>Select metric...</a>
				{{else}}
					<span>{{this.emptyText}}</span>
				{{/if}}
			</div>
		</div>
	</template>
}
